export const LANGUAGE_COOKIE_NAME = 'scenigma_locale';
export const REQUEST_LANGUAGE_HEADER = 'x-scenigma-locale';

export const PUBLIC_LOCALES = [
  { languageCode: 'en-US', slug: 'en', hreflang: 'en', htmlLang: 'en', label: 'English' },
  { languageCode: 'zh-CN', slug: 'zh-hans', hreflang: 'zh-Hans', htmlLang: 'zh-Hans', label: '简体中文' },
  { languageCode: 'zh-TW', slug: 'zh-hant', hreflang: 'zh-Hant', htmlLang: 'zh-Hant', label: '繁體中文' },
  { languageCode: 'ja-JP', slug: 'ja', hreflang: 'ja', htmlLang: 'ja', label: '日本語' },
  { languageCode: 'ko-KR', slug: 'ko', hreflang: 'ko', htmlLang: 'ko', label: '한국어' },
  { languageCode: 'es-ES', slug: 'es', hreflang: 'es', htmlLang: 'es', label: 'Español' },
  { languageCode: 'th-TH', slug: 'th', hreflang: 'th', htmlLang: 'th', label: 'ไทย' },
  { languageCode: 'id-ID', slug: 'id', hreflang: 'id', htmlLang: 'id', label: 'Bahasa Indonesia' },
  { languageCode: 'de-DE', slug: 'de', hreflang: 'de', htmlLang: 'de', label: 'Deutsch' },
  { languageCode: 'ru-RU', slug: 'ru', hreflang: 'ru', htmlLang: 'ru', label: 'Русский' },
  { languageCode: 'vi-VN', slug: 'vi', hreflang: 'vi', htmlLang: 'vi', label: 'Tiếng Việt' },
] as const;

export type AppLanguageCode = (typeof PUBLIC_LOCALES)[number]['languageCode'];

export const DEFAULT_LANGUAGE_CODE: AppLanguageCode = 'en-US';

const localeByLanguageCode = new Map<string, (typeof PUBLIC_LOCALES)[number]>(
  PUBLIC_LOCALES.map((locale) => [locale.languageCode.toLowerCase(), locale]),
);
const localeBySlug = new Map<string, (typeof PUBLIC_LOCALES)[number]>(
  PUBLIC_LOCALES.map((locale) => [locale.slug, locale]),
);

export const LANGUAGE_OPTIONS = PUBLIC_LOCALES.map(({ languageCode: id, label }) => ({ id, label }));

export function resolveLanguageCode(value?: string | null): AppLanguageCode | null {
  if (!value) return null;
  const normalized = value.trim().replaceAll('_', '-').toLowerCase();
  if (!normalized) return null;

  const exact = localeByLanguageCode.get(normalized) ?? localeBySlug.get(normalized);
  if (exact) return exact.languageCode;

  if (normalized === 'zh' || normalized.startsWith('zh-')) {
    return /(?:hant|tw|hk|mo)(?:-|$)/.test(normalized) ? 'zh-TW' : 'zh-CN';
  }

  const prefix = normalized.split('-')[0];
  return PUBLIC_LOCALES.find((locale) => locale.slug === prefix)?.languageCode ?? null;
}

export function normalizeLanguageCode(value?: string | null): AppLanguageCode {
  return resolveLanguageCode(value) ?? DEFAULT_LANGUAGE_CODE;
}

export function getPublicLocale(languageCode?: string | null) {
  const resolved = normalizeLanguageCode(languageCode);
  return PUBLIC_LOCALES.find((locale) => locale.languageCode === resolved) ?? PUBLIC_LOCALES[0];
}

export function languageCodeFromPathname(pathname: string): AppLanguageCode | null {
  const firstSegment = pathname.split('/').filter(Boolean)[0]?.toLowerCase();
  return firstSegment ? localeBySlug.get(firstSegment)?.languageCode ?? null : null;
}

export function stripPublicLocale(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0 || !localeBySlug.has(segments[0].toLowerCase())) return pathname || '/';
  const rest = `/${segments.slice(1).join('/')}`;
  return rest === '/' ? '/discover' : rest;
}

export function isPublicContentPath(pathname: string): boolean {
  const basePath = stripPublicLocale(pathname).replace(/\/$/, '') || '/';
  return basePath === '/discover'
    || basePath === '/guide/haiguitang'
    || basePath === '/support'
    || basePath === '/privacy'
    || basePath === '/terms'
    || basePath.startsWith('/puzzle/');
}

export function localizedPublicPath(pathname: string, languageCode: string): string {
  const basePath = stripPublicLocale(pathname);
  if (!isPublicContentPath(basePath)) return pathname;
  return `/${getPublicLocale(languageCode).slug}${basePath}`;
}

export function publicLanguageAlternates(pathname: string): Record<string, string> {
  const basePath = stripPublicLocale(pathname);
  const languages = Object.fromEntries(
    PUBLIC_LOCALES.map((locale) => [locale.hreflang, `/${locale.slug}${basePath}`]),
  );
  return { ...languages, 'x-default': basePath === '/discover' ? '/' : basePath };
}

export function resolveAcceptLanguage(headerValue?: string | null): AppLanguageCode | null {
  if (!headerValue) return null;
  const preferences = headerValue
    .split(',')
    .map((part, index) => {
      const [tag, ...params] = part.trim().split(';');
      const quality = Number(params.find((param) => param.trim().startsWith('q='))?.trim().slice(2) ?? '1');
      return { tag, quality: Number.isFinite(quality) ? quality : 0, index };
    })
    .sort((a, b) => b.quality - a.quality || a.index - b.index);

  for (const preference of preferences) {
    const languageCode = resolveLanguageCode(preference.tag);
    if (languageCode) return languageCode;
  }
  return null;
}
